import type { Entitlement, PlayerUsage } from "@magicweave/api";

import { countedPlayers, playerStanding } from "./player-standing";
import { calendarDay } from "./when";

/** The rail's own words: its tool group, the credits link's tooltip, and the collapse control. */
export const AI_TOOLS = "AI tools";
export const CREDITS_HEADING = "Credits & usage";
export const COLLAPSE_NAV = "Collapse navigation";
export const EXPAND_NAV = "Expand navigation";

export function creditsTitle(detail: string): string {
  return `${CREDITS_HEADING}. ${detail}`;
}

/** When the allowance next starts again, said "1 Nov"; undefined for a lifetime one. */
export function renewsWords(e: Entitlement): string | undefined {
  if (e.window.kind !== "month") return undefined;
  if (e.window.resets_on) return calendarDay(e.window.resets_on);
  const start = new Date(e.period_start);
  return calendarDay(new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1)));
}

/** The sidebar's one line under the credits meter, short enough for 232px. */
export function railLine(e: Entitlement): string | undefined {
  const { state, remedy } = e.allowance;
  const renews = renewsWords(e);
  switch (state) {
    case "comfortable":
      return undefined;
    case "low":
      return e.window.kind === "month"
        ? "Less than a fifth of this month's AI credits are left."
        : "Less than a fifth of your AI credits are left.";
    case "over":
      return "Past your allowance, billed up to the ceiling you set.";
    case "spent": {
      const paused = renews ? `AI work is paused until ${renews}.` : "AI work is paused.";
      return remedy === "buy_pack"
        ? `${paused} Buy AI credits or upgrade.`
        : `${paused} Move to a larger plan or turn overage on.`;
    }
    case "capped":
      return remedy === "raise_ceiling"
        ? "AI work is paused. Raise your ceiling."
        : "AI work is paused. Move to a larger plan.";
  }
}

/** The sidebar's players row: lifetime players against the plan, and what to do when it is filling. */
interface RailPlayers {
  /** "1,200 / 10K", or "1,200" against no limit. */
  value: string;
  /** 0–1 of the limit, or null when the plan has none. */
  share: number | null;
  tone: "neutral" | "warning" | "critical";
  line?: string;
  label: string;
}

export function railPlayers(usage: PlayerUsage): RailPlayers {
  const counted = countedPlayers(usage);
  const standing = playerStanding({ ...usage, onboarded: counted });
  const limit = usage.limit ?? null;
  const figure = counted.toLocaleString("en-IN");
  const of = limit === null ? "" : limit.toLocaleString("en-IN");
  const base = {
    value: limit === null ? figure : `${figure} / ${compact(limit)}`,
    share: limit === null ? null : Math.min(counted / Math.max(limit, 1), 1),
    label: limit === null ? `${figure} players, no limit` : `${figure} of ${of} players`,
  };
  switch (standing) {
    case "near":
      return {
        ...base,
        tone: "warning",
        line: "Nearly at your plan's players. Upgrade to keep sign-ups open.",
      };
    case "grace": {
      const left = usage.grace
        ? Math.max(usage.grace.extra_allowed - usage.grace.admitted, 0)
        : 0;
      return {
        ...base,
        tone: "warning",
        line: usage.grace
          ? `In grace until ${calendarDay(usage.grace.ends_at)} or ${left.toLocaleString("en-IN")} more ${left === 1 ? "player" : "players"}.`
          : undefined,
      };
    }
    case "full":
    case "closed":
      return {
        ...base,
        tone: "critical",
        line: "New players can't sign up. Upgrade to reopen.",
      };
    default:
      return { ...base, tone: "neutral" };
  }
}

/** A limit as the rail can fit it: 10,000 → "10K", 2,50,000 → "250K". */
function compact(n: number): string {
  return n >= 1000 && n % 1000 === 0 ? `${String(n / 1000)}K` : n.toLocaleString("en-IN");
}
